// Navigation components for The House of Chopras
// Provides reusable navigation elements with terminal aesthetic

import { navigateTo } from './router';

/**
 * Configuration options for the return to house navigation component
 */
export interface ReturnToHouseOptions {
  /** Custom text to display (default: "> RETURN TO HOUSE") */
  text?: string;
  /** Additional CSS class names */
  className?: string;
  /** Whether to use a link instead of a button (default: false) */
  useLink?: boolean;
  /** Callback to run before navigation (return false to cancel) */
  beforeNavigate?: () => boolean | void;
}

/**
 * Creates a "Return to House" navigation element
 * Used on all subpages to navigate back to the main house hub
 *
 * @param options Configuration options
 * @returns HTMLElement - Either a button or anchor element
 *
 * @example
 * // Basic usage
 * const nav = createReturnToHouse();
 * container.appendChild(nav);
 *
 * @example
 * // With custom options
 * const nav = createReturnToHouse({
 *   text: '> BACK TO HOUSE',
 *   className: 'custom-nav',
 *   beforeNavigate: () => {
 *     console.log('Navigating...');
 *     return true;
 *   }
 * });
 */
export function createReturnToHouse(options: ReturnToHouseOptions = {}): HTMLElement {
  const {
    text = '> RETURN TO HOUSE',
    className = '',
    useLink = false,
    beforeNavigate
  } = options;

  const handleClick = (e: Event) => {
    e.preventDefault();

    // Check if navigation should proceed
    if (beforeNavigate) {
      const result = beforeNavigate();
      if (result === false) {
        return;
      }
    }

    navigateTo('house');
  };

  if (useLink) {
    const link = document.createElement('a');
    link.href = '#house';
    link.textContent = text;
    link.className = `return-to-house return-to-house--link ${className}`.trim();
    link.setAttribute('data-corridor', 'house');
    link.setAttribute('aria-label', 'Return to the main house navigation');
    link.addEventListener('click', handleClick);
    return link;
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = text;
  button.className = `return-to-house nav-back ${className}`.trim();
  button.setAttribute('data-corridor', 'house');
  button.setAttribute('aria-label', 'Return to the main house navigation');
  button.addEventListener('click', handleClick);

  return button;
}

/**
 * Creates a navigation wrapper with the return to house component
 * Provides consistent layout and styling for page navigation sections
 *
 * @param options Configuration options for the return button
 * @returns HTMLElement - A nav element containing the return button
 *
 * @example
 * // Add navigation section to a page
 * const navSection = createReturnToHouseNav();
 * container.appendChild(navSection);
 */
export function createReturnToHouseNav(options: ReturnToHouseOptions = {}): HTMLElement {
  const nav = document.createElement('nav');
  nav.className = 'page-nav return-to-house-nav';
  nav.setAttribute('aria-label', 'Page navigation');

  const button = createReturnToHouse(options);
  nav.appendChild(button);

  return nav;
}

/**
 * Appends the return to house navigation to a container element
 * Convenience function that handles finding/creating the nav section
 *
 * @param container The parent container to append to
 * @param options Configuration options for the return button
 * @returns The created navigation element
 *
 * @example
 * // Quickly add navigation to a page container
 * appendReturnToHouse(pageContainer);
 */
export function appendReturnToHouse(
  container: HTMLElement,
  options: ReturnToHouseOptions = {}
): HTMLElement {
  const nav = createReturnToHouseNav(options);
  container.appendChild(nav);
  return nav;
}

/**
 * Sets up return to house navigation on an existing button/link element
 * Useful when the HTML is already defined (e.g., in innerHTML templates)
 *
 * @param element The button or link element to set up
 * @param options Configuration options
 *
 * @example
 * // Setup existing button
 * const backBtn = container.querySelector('.nav-back');
 * if (backBtn) {
 *   setupReturnToHouse(backBtn as HTMLElement);
 * }
 */
export function setupReturnToHouse(
  element: HTMLElement,
  options: ReturnToHouseOptions = {}
): void {
  const { beforeNavigate } = options;

  element.setAttribute('data-corridor', 'house');
  element.setAttribute('aria-label', 'Return to the main house navigation');
  element.classList.add('return-to-house');

  const handleClick = (e: Event) => {
    e.preventDefault();

    if (beforeNavigate) {
      const result = beforeNavigate();
      if (result === false) {
        return;
      }
    }

    navigateTo('house');
  };

  element.addEventListener('click', handleClick);
}
